<template>
	<div class="space-y-6 bg-white border-t border-border-secondary p-4 text-body-s">
		<BranchWidgetOpeningHours aria-describedby="branch-opening-hours-widget" @update:click="handleInfoClick">
			<template #header>
				<h2 id="branch-opening-hours-widget" class="text-h4 font-bold mb-4">
					Otevírací doba
				</h2>
			</template>
		</BranchWidgetOpeningHours>
		<FoodMenuTodayWidget v-if="branchPageAvailability.hasRestaurant" class="space-y-2 lg:space-y-4 text-sm md:text-base">
			<template #header>
				<h2 class="text-h4 font-bold mb-4">
					Denní nabídka restaurace
				</h2>
				<div class="font-semibold capitalize mb-2">
					{{ format(now, 'eeee d. M. yyyy') }}
				</div>
			</template>
			<template #footer>
				<div class="flex gap-2 items-start !mt-4">
					<AbsoluteInternalLink
						:to="branchSpecificPath({name: 'sluzby-restaurace-nabidka'})"
						class="btn btn-primary btn-xs justify-center text-center"
						@click="handleInfoClick('Úplná nabídka | Denní nabídka restaurace')"
					>
						Úplná nabídka
					</AbsoluteInternalLink>
					<AbsoluteInternalLink
						v-if="branchPageAvailability.hasOnlineOrder"
						:to="branchSpecificPath({name: 'online-objednavka-slug', params: {slug: 'restaurace'}})"
						class="btn btn-secondary btn-xs justify-center text-center"
						@click="handleInfoClick('Objednat online | Denní nabídka restaurace')"
					>
						<NuxtIcon
							name="custom/arrow-right"
							class="text-lg transition duration-300"
							filled
							aria-hidden="true"
						/>
						Objednat online
					</AbsoluteInternalLink>
				</div>
			</template>
		</FoodMenuTodayWidget>
		<BranchWidgetGasStation
			aria-describedby="branch-gas-station-widget"
			class="text-sm md:text-base"
			@update:click="handleInfoClick"
			@condition-met="handleConditionMet"
		>
			<template #header>
				<h2 id="branch-gas-station-widget" class="text-h4 font-bold mb-4">
					Čerpací stanice
				</h2>
			</template>
		</BranchWidgetGasStation>

		<CarWashPricelistTable
			v-if="hypermarketDetails && hypermarketDetails.carWash?.open"
			:pricelist="hypermarketDetails.carWash.pricelist[0]"
			aria-describedby="car-wash-pricelist-table-caption"
			class="w-full"
			:pending="hypermarketDetailsLoading"
		>
			<template #header>
				<h2 class="text-h4 font-bold mb-4">
					Mycí linka
				</h2>
			</template>
		</CarWashPricelistTable>
		<AbsoluteInternalLink
			v-if="showMoreInfoButton"
			:to="branchSpecificPath({name: 'sluzby-slug', hash: '#informace', params: {slug: 'cerpaci-stanice-a-myci-linka'}})"
			class="btn btn-secondary btn-xs justify-center text-center max-xs:grow mt-4 xl:shadow-sm"
			:aria-label="`Zobrazit více o pobočce ${selectedHypermarket}`"
			@click="handleInfoClick('Více o čerpacích stanicích a myčkách | Čerpací stanice a myčky')"
		>
			<NuxtIcon
				name="custom/arrow-right"
				class="text-lg transition duration-300"
				filled
				aria-hidden="true"
			/>
			Více o&nbsp;čerpacích stanicích a&nbsp;myčkách
		</AbsoluteInternalLink>
	</div>
</template>

<script setup lang="ts">
	import {format} from 'date-fns';
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '../../../stores/storyblokHypermarkets';

	type Emits = {
		(e: 'update:click', payload: boolean): void
	};

	const emit = defineEmits<Emits>();

	const now = useNow();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const branchSpecificPath = useBranchSpecificPath();

	const {data: hypermarketDetails, loading: hypermarketDetailsLoading} = useHypermarketDetails(selectedHypermarket);
	const {data: branchPageAvailability} = useBranchPageAvailability(selectedHypermarket);

	const showGasStationButton = ref(false);

	const showMoreInfoButton = computed(() => (
		branchPageAvailability.value.hasServices
		&& (showGasStationButton.value || !!(hypermarketDetails.value && hypermarketDetails.value.carWash?.open))
	));

	function closeMenu() {
		emit('update:click', false);
	}

	function handleInfoClick(text: string) {
		trackHeaderEvent('info_in_selector_left', text, {selected_hypermarket: selectedHypermarketInstance.value?.content.name});
		closeMenu();
	}

	function handleConditionMet(isMet: boolean) {
		showGasStationButton.value = isMet;
	}
</script>
